import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useApi, errMsg } from "@/lib/useApi";
import { Plus, Trash2 } from "lucide-react";

const BLANK = {
  family: "UGC_NET", pool: "UGC_PAPER1", subject_code: "", section: "", unit: "", subtopic: "", difficulty: "medium",
  format: "single", stem: "", options: ["A", "B", "C", "D"].map((k) => ({ key: k, text: "" })), correct: [],
  explanation: "", source_type: "manual", source_ref: "",
};

export const Sel = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export default function QuestionEditor({ open, onOpenChange, initial, onSaved }) {
  const api = useApi();
  const [f, setF] = useState(BLANK);
  const [subjects, setSubjects] = useState([]);
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setF(initial ? { ...BLANK, ...initial, subject_code: initial.subject_code || "" } : BLANK); setDups(null); } }, [open, initial]);
  useEffect(() => { api.get(`/subjects?family=${f.family}`).then((r) => setSubjects(r.data)); }, [f.family, api]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setFamily = (v) => setF((s) => ({ ...s, family: v, pool: v === "UGC_NET" ? "UGC_PAPER1" : "CSIR_PARTA", subject_code: "", section: "" }));
  const setOpt = (i, text) => set("options", f.options.map((o, j) => (j === i ? { ...o, text } : o)));
  const toggleCorrect = (k) => set("correct", f.format === "single" ? [k] : f.correct.includes(k) ? f.correct.filter((x) => x !== k) : [...f.correct, k]);
  const payload = () => ({ ...f, subject_code: f.pool === "SUBJECT" ? f.subject_code : null, section: f.pool === "SUBJECT" && f.family === "CSIR_NET" ? f.section : null,
    ...(initial ? { expected_version: initial.version } : {}) });

  const check = async () => {
    try { setDups((await api.post("/admin/questions/check-duplicates", payload())).data.duplicates); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const r = initial ? await api.put(`/admin/questions/${initial.id}`, payload()) : await api.post("/admin/questions", payload());
      const d = r.data.duplicates || [];
      toast.success(initial ? "Saved as new version" : "Draft created");
      if (d.length) toast.warning(`${d.length} possible duplicate(s) flagged`);
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" data-testid="question-editor">
        <DialogHeader><DialogTitle>{initial ? `Edit question (v${initial.version} → v${initial.version + 1})` : "New question (draft)"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field l="Exam family"><Sel value={f.family} onChange={setFamily} options={[["UGC_NET", "UGC NET"], ["CSIR_NET", "CSIR NET"]]} testid="qe-family" /></Field>
          <Field l="Pool"><Sel value={f.pool} onChange={(v) => set("pool", v)} testid="qe-pool"
            options={f.family === "UGC_NET" ? [["UGC_PAPER1", "Paper I (shared)"], ["SUBJECT", "Paper II (subject)"]] : [["CSIR_PARTA", "Part A (shared)"], ["SUBJECT", "Part B/C (subject)"]]} /></Field>
          <Field l="Difficulty"><Sel value={f.difficulty} onChange={(v) => set("difficulty", v)} options={[["easy", "Easy"], ["medium", "Medium"], ["hard", "Hard"]]} testid="qe-difficulty" /></Field>
          {f.pool === "SUBJECT" && <Field l="Subject" className="sm:col-span-2"><Sel value={f.subject_code} onChange={(v) => set("subject_code", v)} testid="qe-subject"
            options={[["", "Select…"], ...subjects.map((s) => [s.code, `${s.code} · ${s.name.slice(0, 60)}`])]} /></Field>}
          {f.pool === "SUBJECT" && f.family === "CSIR_NET" && <Field l="Part"><Sel value={f.section || ""} onChange={(v) => set("section", v)} options={[["", "Select…"], ["B", "Part B"], ["C", "Part C"]]} testid="qe-section" /></Field>}
          <Field l="Unit"><Input value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="qe-unit" /></Field>
          <Field l="Subtopic"><Input value={f.subtopic} onChange={(e) => set("subtopic", e.target.value)} data-testid="qe-subtopic" /></Field>
          <Field l="Answer format"><Sel value={f.format} onChange={(v) => setF((s) => ({ ...s, format: v, correct: v === "single" ? s.correct.slice(0, 1) : s.correct }))} options={[["single", "Single answer"], ["multiple", "Multiple answers"]]} testid="qe-format" /></Field>
        </div>
        <Field l="Question stem"><Textarea rows={4} value={f.stem} onChange={(e) => set("stem", e.target.value)} data-testid="qe-stem" /></Field>
        <div className="space-y-2">
          <Label className="text-xs text-slate-500">Options — tick the correct answer(s)</Label>
          {f.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <Checkbox checked={f.correct.includes(o.key)} onCheckedChange={() => toggleCorrect(o.key)} data-testid={`qe-correct-${o.key}`} />
              <span className="w-5 font-semibold text-sm">{o.key}</span>
              <Input value={o.text} onChange={(e) => setOpt(i, e.target.value)} data-testid={`qe-option-${o.key}`} />
              {f.options.length > 2 && <Button size="icon" variant="ghost" onClick={() => set("options", f.options.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ))}
          {f.options.length < 6 && <Button size="sm" variant="ghost" onClick={() => set("options", [...f.options, { key: "ABCDEF"[f.options.length], text: "" }])}><Plus className="h-4 w-4 mr-1" />Option</Button>}
        </div>
        <Field l="Explanation"><Textarea rows={2} value={f.explanation} onChange={(e) => set("explanation", e.target.value)} data-testid="qe-explanation" /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field l="Provenance"><Sel value={f.source_type} onChange={(v) => set("source_type", v)} options={[["manual", "Written by staff"], ["previous_year", "Previous-year paper"], ["import", "Imported"], ["ai", "AI generated"]]} testid="qe-source-type" /></Field>
          <Field l="Source reference"><Input value={f.source_ref} onChange={(e) => set("source_ref", e.target.value)} placeholder="e.g. Dec 2023 paper, Q14" data-testid="qe-source-ref" /></Field>
        </div>
        {dups && <div className="text-sm rounded-lg border border-slate-200 p-3" data-testid="qe-duplicates">
          {dups.length ? dups.map((d) => <p key={d.id} className="text-amber-700">{Math.round(d.similarity * 100)}% · {d.stem} ({d.status})</p>) : <p className="text-emerald-700">No similar questions found.</p>}
        </div>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={check} data-testid="qe-check-duplicates">Check duplicates</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="qe-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const Field = ({ l, children, className = "" }) => (
  <div className={`space-y-1 ${className}`}><Label className="text-xs text-slate-500">{l}</Label>{children}</div>
);
